import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Users, Moon, ArrowLeft, Check, Send, CheckCircle2 } from 'lucide-react';
import { estimatorRegions, estimatorStyles, estimatorAddons } from '../data';
import { createEnquiry } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingWhatsApp from '../components/FloatingWhatsApp';

export default function Estimator() {
  const { toast } = useToast();
  const [region, setRegion] = useState(estimatorRegions[1].name);
  const [travelers, setTravelers] = useState(2);
  const [nights, setNights] = useState(6);
  const [style, setStyle] = useState('Comfort');
  const [addons, setAddons] = useState({ flights: true, visa: false, insurance: true, transfers: true });
  const [lead, setLead] = useState({ name: '', email: '' });
  const [sent, setSent] = useState(false);

  const estimate = useMemo(() => {
    const r = estimatorRegions.find((x) => x.name === region);
    const s = estimatorStyles.find((x) => x.name === style);
    const base = r.perNight * nights * s.mult;
    const perPerson = Math.round(base);
    let addonTotal = 0;
    estimatorAddons.forEach((a) => { if (addons[a.key]) addonTotal += a.cost; });
    const total = perPerson * travelers + addonTotal * travelers;
    return { perPerson, addonTotal, total };
  }, [region, travelers, nights, style, addons]);

  const fmt = (n) => '\u20B9' + n.toLocaleString('en-IN');

  const submit = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.email) return;
    try {
      await createEnquiry({
        name: lead.name,
        email: lead.email,
        destination: region,
        source: 'estimator',
        message: `Estimator quote request \u2014 ${travelers} travellers, ${nights} nights, ${style} style. Estimated total: ${fmt(estimate.total)}.`,
      });
      setSent(true);
      toast({ title: 'Estimate sent!', description: 'Our team will share an exact quote shortly.' });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    }
  };

  return (
    <div className="bg-white">
      <Header />
      <section className="relative pt-32 pb-16 bg-[#0B2545] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08] bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=60')" }} />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-4"><ArrowLeft size={16} /> Back to home</Link>
          <p className="section-eyebrow text-[#ff9a6a] mb-2"><span className="h-px w-8 bg-[#EA5019] inline-block" /> TRIP COST ESTIMATOR</p>
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white flex items-center gap-3"><Calculator className="text-[#ff7a45]" size={40} /> Get an Instant Ballpark Price</h1>
          <p className="text-white/75 mt-3 max-w-2xl">Adjust the options below to see a live estimate. It's a guide price {"\u2014"} request a free exact quote and our experts will tailor it to you.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 py-14 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <label className="block text-sm font-semibold text-[#0B2545] mb-3">Destination Region</label>
            <div className="flex flex-wrap gap-2">
              {estimatorRegions.map((r) => (
                <button key={r.name} onClick={() => setRegion(r.name)} className={`px-4 py-2 rounded-full text-sm font-medium border transition ${region === r.name ? 'bg-[#0B2545] text-white border-[#0B2545]' : 'bg-white text-[#0B2545] border-slate-200 hover:border-[#EA5019]'}`}>{r.name}</button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-[#0B2545] mb-2"><Users size={16} className="text-[#EA5019]" /> Travellers: {travelers}</label>
              <input type="range" min="1" max="12" value={travelers} onChange={(e) => setTravelers(Number(e.target.value))} className="w-full accent-[#EA5019]" />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-[#0B2545] mb-2"><Moon size={16} className="text-[#EA5019]" /> Nights: {nights}</label>
              <input type="range" min="2" max="21" value={nights} onChange={(e) => setNights(Number(e.target.value))} className="w-full accent-[#EA5019]" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#0B2545] mb-3">Travel Style</label>
            <div className="grid grid-cols-3 gap-3">
              {estimatorStyles.map((s) => (
                <button key={s.name} onClick={() => setStyle(s.name)} className={`px-4 py-3 rounded-xl text-sm font-semibold border transition ${style === s.name ? 'bg-[#EA5019]/10 text-[#EA5019] border-[#EA5019]' : 'bg-white text-[#0B2545] border-slate-200 hover:border-[#EA5019]/50'}`}>{s.name}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#0B2545] mb-3">Add-ons (per traveller)</label>
            <div className="grid sm:grid-cols-2 gap-3">
              {estimatorAddons.map((a) => (
                <button key={a.key} onClick={() => setAddons({ ...addons, [a.key]: !addons[a.key] })} className={`flex items-center justify-between px-4 py-3 rounded-xl border text-sm transition ${addons[a.key] ? 'bg-[#0B2545]/[0.04] border-[#0B2545]' : 'bg-white border-slate-200 hover:border-slate-300'}`}>
                  <span className="flex items-center gap-2 font-medium text-[#0B2545]"><span className={`grid place-items-center h-5 w-5 rounded border ${addons[a.key] ? 'bg-[#EA5019] border-[#EA5019] text-white' : 'border-slate-300'}`}>{addons[a.key] && <Check size={13} />}</span>{a.label}</span>
                  <span className="text-slate-400">{fmt(a.cost)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <aside>
          <div className="sticky top-28 bg-white rounded-2xl border border-slate-100 shadow-[0_18px_44px_rgba(11,37,69,0.12)] p-6">
            <p className="text-sm text-slate-400">Estimated total for {travelers} traveller{travelers > 1 ? 's' : ''}</p>
            <p className="font-heading text-4xl font-extrabold text-[#0B2545] mt-1">{fmt(estimate.total)}</p>
            <p className="text-xs text-slate-400 mt-1 mb-5">Approx {fmt(Math.round(estimate.total / travelers))} per person {"\u00b7"} guide price only</p>

            <form onSubmit={submit} className="space-y-3">
              <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} required placeholder="Your name" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0B2545] outline-none focus:border-[#EA5019] focus:ring-2 focus:ring-[#EA5019]/20" />
              <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} required type="email" placeholder="Email address" className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm text-[#0B2545] outline-none focus:border-[#EA5019] focus:ring-2 focus:ring-[#EA5019]/20" />
              <button type="submit" className={`w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-semibold text-white transition ${sent ? 'bg-[#22a06b]' : 'bg-[#EA5019] hover:bg-[#cf4413]'}`}>{sent ? (<><CheckCircle2 size={18} /> Sent!</>) : (<><Send size={17} /> Get Exact Quote</>)}</button>
            </form>
            <p className="text-[11px] text-slate-400 mt-3 text-center">No obligation. Our experts reply within 24 hours.</p>
          </div>
        </aside>
      </section>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
